// ============================================================================
// LE VOCABULAIRE — quel MOT afficher pour un champ, selon le métier et la langue.
//
// POURQUOI CE FICHIER EXISTE. « Saison » n'a aucun sens chez un caviste : c'est « Millésime » qui doit
// apparaître — et « Añada » pour son vendeur espagnol. Deux chantiers répondaient séparément à cette
// question (le renommage par métier, la traduction par langue) : tenus à part, ils auraient donné DEUX
// résolveurs concurrents du même libellé, et un jour l'un des deux aurait eu tort. Il n'y en a qu'un,
// et il vit ici parce que trois surfaces en dépendent — la Console qui pose les défauts, le back-office
// et la caisse qui les affichent. Une règle que tous doivent connaître EST le contrat (§24.3bis).
//
// LA CHAÎNE (CADRAGE-Referentiels-Vocabulaire §6ter) :
//   ①  champ + métier  →  TERME      (Console — « saison » vaut `vintage` pour un caviste)
//   ②  terme + langue  →  TEXTE      (dictionnaire — `vintage` en `es` = « Añada »)
//   ③  surcharge du commerçant, DANS SA LANGUE  (« Pitouf », et c'est son droit)
// ⚠️ CE N'EST PAS UNE MATRICE champs × métiers × langues : ajouter un métier n'ajoute aucune traduction
//    tant qu'il réutilise des termes existants. Et le magasin ne connaît JAMAIS son métier — il reçoit
//    le texte déjà résolu ; ni le back-office ni la caisse n'ont besoin de savoir de quel commerce il
//    relève.
//
// CE QUI DESCEND DANS `setting`, deux espaces de clés qui ne se touchent jamais :
//   · `vocab_default.<champ>.<langue>` — écrit par la CONSOLE (propagation), le texte résolu ①+② ;
//   · `vocab.<champ>.<langue>`         — écrit par le COMMERÇANT, sa surcharge.
// ⚠️ UNE CASCADE, PAS UNE COPIE : la propagation réécrit `vocab_default.*` et n'approche jamais
//    `vocab.*`. Un défaut corrigé en Console atteint donc tout le parc, sauf la case que le commerçant
//    s'est appropriée. Un magasin qui n'a rien surchargé n'a AUCUNE ligne `vocab.*`.
// ⚠️ LA SURCHARGE EST UN LITTÉRAL, DONC ELLE A UNE LANGUE : « Pitouf » tapé en français ne dit rien
//    au vendeur espagnol, qui garde « Añada ». D'où le suffixe de langue sur la clé — gratuit
//    aujourd'hui, une migration s'il arrivait après (Forme J : une clé persistée ne se renomme pas).
//
// CE QUE LE CODE GARDE, ET C'EST TOUT CE QU'IL GARDE : le FILET — le mot de la plateforme par champ et
// par langue, pour qu'un magasin où rien n'est encore descendu affiche un mot et JAMAIS une clé. Il
// est aussi la graine du dictionnaire Console : la vérité est en base, le code la précède sans la
// contredire.
//
// ⚠️ LA CLÉ DU CHAMP EST EN ANGLAIS ET NE BOUGE JAMAIS (`season`) : c'est la colonne, le nom dans le
//    code, le champ de l'API. Le commerçant renomme l'ÉTIQUETTE, jamais la chose — c'est ce qui rend
//    « Pitouf » sans danger pour une requête, une reprise ou un scellement.
// ============================================================================
import { normalizeCode } from './master-data-ids';

/** Les langues d'interface retenues (SPEC §22.1 : FR défaut, EN, ES — extensible). */
export const VOCABULARY_LANGS = ['fr', 'en', 'es'] as const;
export type VocabularyLang = (typeof VOCABULARY_LANGS)[number];
/** La langue de repli de toute la plateforme — celle des opérateurs d'aujourd'hui. */
export const DEFAULT_VOCABULARY_LANG: VocabularyLang = 'fr';

/** Une valeur reçue de l'extérieur (réglage, en-tête, préférence) est-elle une langue connue ? */
export function isVocabularyLang(value: unknown): value is VocabularyLang {
  return typeof value === 'string' && (VOCABULARY_LANGS as readonly string[]).includes(value);
}

/**
 * LES CHAMPS QUI ONT UN MOT — la liste de référence, dans l'ordre où une fiche les présente.
 *
 * ⚠️ LA CLÉ EST L'IDENTITÉ : elle nomme le champ dans `setting` (`vocab.season.fr`), donc elle est
 *    persistée dans chaque base magasin dès la première surcharge. Elle se MIGRE, ne se renomme pas.
 * ⚠️ LE MOT PAR DÉFAUT EST LE FILET, PAS LA VÉRITÉ : la Console porte le dictionnaire vivant. Corriger
 *    un mot ICI ne change rien à un magasin qui a déjà reçu le sien — c'est voulu.
 * ⚠️ `aliases` = LES AUTRES MOTS QUE LE CODE EMPLOIE AUJOURD'HUI POUR LE MÊME CHAMP, relevés au
 *    parseur : « Rayon » et « Secteur », « Couleur » et « Coloris », « Gencode » et « Code-barres »,
 *    « Désignation » et « Libellé »… — deux tables écrites deux fois qui ont déjà divergé (Forme A).
 *    Ils ne servent qu'à l'audit anti-rechute, pour que ces sites-là comptent aussi comme « en dur »
 *    et migrent vers le vocabulaire au lieu d'y échapper. Le mécanisme les fait disparaître par
 *    construction : UN mot par champ, résolu à un seul endroit.
 * ⚠️ « Saison » désigne ICI la collection d'un article. La fenêtre légale des SOLDES porte le même mot
 *    et ne se renomme JAMAIS (art. L310-3) : elle n'est pas un champ de ce catalogue — et elle
 *    n'apparaît d'ailleurs jamais comme libellé nu, ce qui la tient hors de portée de l'audit.
 * ⚠️ LES DRAPEAUX (« Hors suivi de stock », « Remises interdites »…) N'Y SONT PAS, à dessein : ce sont
 *    des PHRASES à polarité, dont `article-flags.ts` est le seul propriétaire depuis l'incident de
 *    l'inversion. Un second propriétaire recréerait exactement ce défaut.
 */
export const VOCABULARY_FIELDS = [
  { key: 'name', column: 'name', defaults: { fr: 'Désignation', en: 'Description', es: 'Descripción' }, aliases: ['Libellé'] },
  { key: 'articleCode', column: 'article_code', defaults: { fr: 'Code article', en: 'Item code', es: 'Código de artículo' }, aliases: [] },
  { key: 'modelCode', column: 'model_code', defaults: { fr: 'Code modèle', en: 'Model code', es: 'Código de modelo' }, aliases: ['Référence modèle'] },
  { key: 'gencode', column: 'gencode', defaults: { fr: 'Gencode', en: 'Barcode', es: 'Código de barras' }, aliases: ['Code-barres'] },
  { key: 'collection', column: 'collection_id', defaults: { fr: 'Collection', en: 'Collection', es: 'Colección' }, aliases: [] },
  { key: 'family', column: 'family_id', defaults: { fr: 'Famille', en: 'Family', es: 'Familia' }, aliases: [] },
  { key: 'subFamily', column: 'sub_family_id', defaults: { fr: 'Sous-famille', en: 'Sub-family', es: 'Subfamilia' }, aliases: [] },
  { key: 'sector', column: 'sector', defaults: { fr: 'Secteur', en: 'Sector', es: 'Sector' }, aliases: ['Rayon'] },
  { key: 'supplier', column: 'supplier', defaults: { fr: 'Fournisseur', en: 'Supplier', es: 'Proveedor' }, aliases: [] },
  { key: 'brand', column: 'brand', defaults: { fr: 'Marque', en: 'Brand', es: 'Marca' }, aliases: [] },
  { key: 'season', column: 'season', defaults: { fr: 'Saison', en: 'Season', es: 'Temporada' }, aliases: [] },
  { key: 'sizeGrid', column: 'size_grid_id', defaults: { fr: 'Grille de tailles', en: 'Size grid', es: 'Tabla de tallas' }, aliases: [] },
  { key: 'sizeGrid2', column: 'size_grid2_id', defaults: { fr: 'Second axe', en: 'Second axis', es: 'Segundo eje' }, aliases: [] },
  { key: 'size', column: 'size', defaults: { fr: 'Taille', en: 'Size', es: 'Talla' }, aliases: [] },
  { key: 'color', column: 'color', defaults: { fr: 'Coloris', en: 'Colour', es: 'Color' }, aliases: ['Couleur'] },
  { key: 'weight', column: 'weight_g', defaults: { fr: 'Poids', en: 'Weight', es: 'Peso' }, aliases: ['Poids — grammes'] },
  { key: 'salePriceInclTax', column: 'sale_price_incl_tax_cents', defaults: { fr: 'Prix de vente TTC', en: 'Retail price (incl. VAT)', es: 'Precio de venta (IVA incl.)' }, aliases: ['Prix TTC', 'PV TTC', 'PV TTC (€)', 'Prix de vente', 'Prix'] },
  { key: 'purchasePriceExclTax', column: 'purchase_price_excl_tax_fine', defaults: { fr: 'Prix d’achat', en: 'Purchase price', es: 'Precio de compra' }, aliases: ['Prix d’achat HT', 'PA HT'] },
  { key: 'vatRate', column: 'vat_rate_bp', defaults: { fr: 'TVA', en: 'VAT', es: 'IVA' }, aliases: ['Taux de TVA'] },
  { key: 'purchaseRegime', column: 'purchase_regime', defaults: { fr: 'Régime d’achat', en: 'Purchase regime', es: 'Régimen de compra' }, aliases: [] },
  { key: 'originCountry', column: 'origin_country', defaults: { fr: 'Pays d’origine', en: 'Country of origin', es: 'País de origen' }, aliases: ['Origine'] },
  { key: 'customsCode', column: 'customs_code', defaults: { fr: 'Nomenclature douanière', en: 'Customs code', es: 'Código aduanero' }, aliases: ['Code douanier'] },
  { key: 'composition', column: 'composition', defaults: { fr: 'Composition', en: 'Composition', es: 'Composición' }, aliases: [] },
] as const satisfies readonly { key: string; column: string; defaults: Record<VocabularyLang, string>; aliases: readonly string[] }[];

export type VocabularyFieldKey = (typeof VOCABULARY_FIELDS)[number]['key'];
/** Les clés seules — pour les boucles et les gardes. */
export const VOCABULARY_FIELD_KEYS: readonly VocabularyFieldKey[] = VOCABULARY_FIELDS.map((f) => f.key);

/** Un champ connu du catalogue ? (une clé inconnue est un mot que personne n'affiche) */
export function isVocabularyFieldKey(value: unknown): value is VocabularyFieldKey {
  return typeof value === 'string' && (VOCABULARY_FIELD_KEYS as readonly string[]).includes(value);
}

/** Le mot affiché pour chaque champ, dans UNE langue — ce que reçoit un écran. */
export type Vocabulary = Readonly<Record<VocabularyFieldKey, string>>;

// ── LES CLÉS PERSISTÉES DANS `setting` ────────────────────────────────────────────────────────────
// ⚠️ Deux préfixes distincts, et le second ne CONTIENT pas le premier (`vocab_default.` ne commence pas
//    par `vocab.`) : un lecteur qui testerait `startsWith('vocab.')` ne confondra jamais les couches.

/** La couche d'une ligne `setting` de vocabulaire. */
export type VocabularyLayer = 'override' | 'default';
/** Surcharge du commerçant — `vocab.<champ>.<langue>`. */
export const VOCABULARY_OVERRIDE_PREFIX = 'vocab.';
/** Défaut descendu par la Console — `vocab_default.<champ>.<langue>`. */
export const VOCABULARY_DEFAULT_PREFIX = 'vocab_default.';

/** Le nom de la ligne `setting` qui porte ce mot, pour cette couche. */
export function vocabularySettingKey(layer: VocabularyLayer, field: VocabularyFieldKey, lang: VocabularyLang): string {
  return `${layer === 'override' ? VOCABULARY_OVERRIDE_PREFIX : VOCABULARY_DEFAULT_PREFIX}${field}.${lang}`;
}

/** Une clé persistée du vocabulaire, décomposée. */
export interface VocabularySettingName {
  name: string;
  layer: VocabularyLayer;
  field: VocabularyFieldKey;
  lang: VocabularyLang;
}

/**
 * TOUTES les clés `setting` du vocabulaire — champs × langues × couches — dans un ordre stable.
 * ⚠️ C'est LA liste que le catalogue des réglages spreade : une clé qui n'y est pas ne VOYAGE pas
 *    (le canal `settings` ne descend que ce que le catalogue connaît), et une clé recopiée à la main
 *    divergerait au premier champ ajouté.
 */
export function vocabularySettingNames(): VocabularySettingName[] {
  const out: VocabularySettingName[] = [];
  for (const layer of ['default', 'override'] as const) {
    for (const f of VOCABULARY_FIELDS) {
      for (const lang of VOCABULARY_LANGS) out.push({ name: vocabularySettingKey(layer, f.key, lang), layer, field: f.key, lang });
    }
  }
  return out;
}

/** Ce qu'une ligne `setting` désigne — ou `null` si ce n'est pas du vocabulaire, ou pas un vocabulaire connu. */
export function parseVocabularySettingKey(
  name: string,
): { layer: VocabularyLayer; field: VocabularyFieldKey; lang: VocabularyLang } | null {
  let layer: VocabularyLayer;
  let rest: string;
  if (name.startsWith(VOCABULARY_DEFAULT_PREFIX)) { layer = 'default'; rest = name.slice(VOCABULARY_DEFAULT_PREFIX.length); }
  else if (name.startsWith(VOCABULARY_OVERRIDE_PREFIX)) { layer = 'override'; rest = name.slice(VOCABULARY_OVERRIDE_PREFIX.length); }
  else return null;
  const parts = rest.split('.');
  if (parts.length !== 2) return null;
  const [field, lang] = parts;
  // ⚠️ Un champ ou une langue inconnus rendent `null`, jamais une erreur : une ligne qu'une version
  //    future aura écrite ne doit pas faire tomber la lecture d'une version qui ne la connaît pas.
  if (!isVocabularyFieldKey(field) || !isVocabularyLang(lang)) return null;
  return { layer, field, lang };
}

// ── LA RÉSOLUTION ─────────────────────────────────────────────────────────────────────────────────

/** Une ligne de `setting`, telle que la base la rend. */
export interface VocabularySettingRow {
  name: string;
  value: string | null;
}

/**
 * La longueur maximale d'une clé de terme — UNE constante, dont le motif ci-dessous et la dérivation
 * d'une clé depuis un mot tirent tous deux leur borne. Écrite deux fois, une troncature à 41 devant
 * un motif à 40 produirait une clé que la dérivation croit bonne et que la validation refuse.
 */
export const VOCABULARY_TERM_KEY_MAX_LENGTH = 41;

/**
 * La forme d'une clé de terme du dictionnaire : un identifiant, pas un mot. Minuscule en tête, puis
 * lettres, chiffres, souligné — 2 à `VOCABULARY_TERM_KEY_MAX_LENGTH` caractères.
 * ⚠️ Même alphabet que les clés de champ du catalogue (`articleCode`, `sizeGrid2`) : un terme de
 *    plateforme EST une clé de champ, les deux doivent passer la même règle.
 * ⚠️ DANS LE CONTRAT parce que deux consommateurs l'appliquent — `termKeyFromWord` ci-dessous, qui ne
 *    rend jamais une clé que le motif refuserait, et le serveur de la Console pour une clé explicite
 *    (chemin d'outillage). Une copie de chaque côté divergerait sans que rien ne le signale : la
 *    dérivation produirait ce que la validation refuse, ou l'inverse.
 */
export const VOCABULARY_TERM_KEY_RE = new RegExp(`^[a-z][a-zA-Z0-9_]{1,${VOCABULARY_TERM_KEY_MAX_LENGTH - 1}}$`);

/**
 * LA CLÉ D'UN TERME, DÉRIVÉE DE SON MOT FRANÇAIS — « Millésime » → `millesime`, « Prix d’achat » →
 * `prix_d_achat`, « Añada » → `anada`. `null` quand aucune clé ne s'en tire (« 2024 », « x », du blanc).
 *
 * ⚠️ POURQUOI ELLE EXISTE : la clé technique n'est plus jamais TAPÉE. Un opérateur qui doit inventer
 *    `vintage` pour dire « Millésime » expose la mécanique à l'envers ; il choisit un mot, la clé suit.
 *    Elle reste ce qu'elle était — un identifiant persisté, en base et dans `trade_vocabulary` — et
 *    ne se recalcule jamais après coup : corriger le mot ne renomme pas la clé (Forme J).
 * ⚠️ LE PLIAGE EST CELUI DES CODES DE DONNÉES MAÎTRES (`normalizeCode`), pas une seconde recette :
 *    même NFD, mêmes accents retirés, même minuscule, même repli des suites hors [a-z0-9]. Seul
 *    l'alphabet de sortie diffère — le souligné du motif de clé à la place du tiret — plus la borne
 *    et l'exigence d'une lettre en tête, que `VOCABULARY_TERM_KEY_RE` porte déjà.
 * ⚠️ S'EXÉCUTE EN CONSOLE (Node, navigateur), jamais sur la tablette : la caisse ne crée pas de terme.
 * ⚠️ NE GARANTIT PAS L'UNICITÉ : deux mots peuvent plier sur la même clé (« Été » et « Ete »), et
 *    la clé d'un champ du catalogue est prise par construction. C'est le serveur, qui voit la base,
 *    qui suffixe.
 */
export function termKeyFromWord(word: string): string | null {
  const key = normalizeCode(word).replace(/-/g, '_').slice(0, VOCABULARY_TERM_KEY_MAX_LENGTH).replace(/_+$/, '');
  // Le motif porte les deux refus restants : une lettre en tête, deux caractères au moins.
  return VOCABULARY_TERM_KEY_RE.test(key) ? key : null;
}

/**
 * Un mot posé est-il UTILISABLE ? Vide ou blanc = non posé.
 * ⚠️ C'est la SEULE règle que le commerçant ne peut pas surcharger : une surcharge vide retombe sur
 *    le défaut, jamais sur du blanc ni sur la clé technique. Sur tout le reste — « Pitouf » compris —
 *    aucun jugement.
 * ⚠️ EXPORTÉE parce que deux écrans l'avaient recopiée (la vue « aucune surcharge » du back-office, la
 *    saisie du commerçant) : une règle écrite deux fois finit par diverger, et ici la divergence
 *    ferait annoncer une surcharge que rien n'affiche.
 */
export function isUsableWord(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * LA CASCADE, sur des valeurs déjà lues : le premier candidat utilisable, trimé, sinon le filet.
 * C'est la règle de `resolveVocabulary` isolée pour qui tient déjà les trois étages en main (l'écran
 * qui recalcule pendant la frappe) — la même, jamais une seconde.
 */
export function firstUsableWord(fallback: string, ...candidates: (string | null | undefined)[]): string {
  for (const c of candidates) if (isUsableWord(c)) return c.trim();
  return fallback;
}

/** Le filet seul — ce qu'affiche un magasin où rien n'est encore descendu. */
export function platformVocabulary(lang: VocabularyLang): Vocabulary {
  const out = {} as Record<VocabularyFieldKey, string>;
  for (const f of VOCABULARY_FIELDS) out[f.key] = f.defaults[lang];
  return out;
}

/**
 * Résout le vocabulaire d'UNE langue à partir des lignes `setting` d'un magasin.
 *
 * L'ordre, et il ne se discute pas : la surcharge du magasin dans CETTE langue, sinon le défaut descendu
 * dans cette langue, sinon le filet de la plateforme. Un mot vide à un étage passe à l'étage suivant.
 * ⚠️ Les lignes qui ne sont pas du vocabulaire — ou qui parlent d'un champ ou d'une langue inconnus —
 *    sont ignorées : on peut passer la table `setting` entière.
 */
export function resolveVocabulary(rows: readonly VocabularySettingRow[], lang: VocabularyLang): Vocabulary {
  const override = new Map<VocabularyFieldKey, string>();
  const descended = new Map<VocabularyFieldKey, string>();
  for (const row of rows) {
    const key = parseVocabularySettingKey(row.name);
    if (!key || key.lang !== lang || !isUsableWord(row.value)) continue;
    // Le mot brut : c'est la cascade (firstUsableWord) qui trime UNE fois ce qu'elle retient.
    (key.layer === 'override' ? override : descended).set(key.field, row.value);
  }
  const out = {} as Record<VocabularyFieldKey, string>;
  for (const f of VOCABULARY_FIELDS) out[f.key] = firstUsableWord(f.defaults[lang], override.get(f.key), descended.get(f.key));
  return out;
}

/** Le mot d'UN champ — même cascade que `resolveVocabulary`, pour qui n'a besoin que d'un mot. */
export function resolveFieldLabel(field: VocabularyFieldKey, lang: VocabularyLang, rows: readonly VocabularySettingRow[]): string {
  return resolveVocabulary(rows, lang)[field];
}
